import {Timeline} from "./Timeline";
import TimelineChannel from "./TimelineChannel";
import store, {StoreActions} from "../../state/store";
import {EasingTypeToFunctionMap, TimelineEffectorTypes} from '../../globals';
import MathUtils from "../utilities/MathUtils";
import {IReversibleTimelineElement, TimelineElementTypes} from "../../interfaces/Timeline";
import SceneObjectUtils from "../utilities/SceneObjectUtils";
import Engine from "../Engine";
import {assert} from "../utilities/ErrorHandling";
import { getUuidv4 } from "../utilities/Misc";
import {EffectorColorMap, EffectorMap, timelineElementMap} from ".";

export interface TimelineFragmentData {
	fragmentId: string;
	name: string;
	startTime: number;
	endTime: number;
	effectorType: string;
}

export interface TimelineFragmentSaveData {
	id: string;
	name: string;
	channelId: string;
	timelineId: string;
	startTime: Date;
	endTime: Date;
	effectorType: TimelineEffectorTypes;
	uiColor: string;
	effector: any; // assuming `effector.save()` returns an unknown type
}


const timelineFragmentDataObject: TimelineFragmentData = {
	fragmentId: "",
	name: "",
	startTime: 0,
	endTime: 1,
	effectorType: "",
};

export interface TimelineFragmentExportData {
	// TODO is this the same as TimelineFragmentData?
	startTime: number;
	endTime: number;
	effectorType: string;
	persistentSceneObjectIds?: string[];
	nodes?: number[];
}

export class TimelineFragment implements IReversibleTimelineElement
{
	#id : string;
	#channel: TimelineChannel;
	#effector;
	private fragmentTime: number;
	private previousFragmentTime: number;
	get previousTime() { return this.previousFragmentTime; }
	uiInfo: { color: any; isSelected: boolean };
	timeline: Timeline;

	constructor(name, channel: TimelineChannel, effectorType: TimelineEffectorTypes, startTime = 0, endTime = 1)
	{
		this.#id = getUuidv4();
		this.#channel = channel;
		this.#effector = this.createEffector(effectorType);
		this.fragmentTime = 0;
		this.previousFragmentTime = 0;

		const dataObject = {
			...timelineFragmentDataObject,
			fragmentId: this.id,
			name: name,
			startTime: startTime,
			endTime: endTime,
			effectorType: effectorType,
			effectorData: this.effector ? this.effector.save() : {},
		};
		store.dispatch(StoreActions.timeline.setElementData({ dataId: this.id, data: dataObject }));

		this.uiInfo = {
			color: EffectorColorMap.get(this.effectorType),
			isSelected: false,
		};

		this.timeline = this.channel.object.track.timeline;

		timelineElementMap.set(this.id, this);

		if('getInitialDuration' in this.effector)
		{
			const duration = this.effector.getInitialDuration();
			if (duration)
			{
				this.endTime = this.startTime + duration;
			}
		}
	}

	getDataObject()
	{
		return store.getState().timelineModule.elementData[this.id];
	}

	get id()
	{
		return this.#id;
	}

	get channel()
	{
		return this.#channel;
	}

	get effector()
	{
		return this.#effector;
	}

	get startTime()
	{
		return this.getDataObject().startTime;
	}

	set startTime(time)
	{
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: this.id, propertyName: "startTime", propertyValue: time }));

	}

	get endTime()
	{
		return this.getDataObject().endTime;
	}

	set endTime(time)
	{
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: this.id, propertyName: "endTime", propertyValue: time }));
	}

	get effectorType()
	{
		return this.getDataObject().effectorType;
	}

	set effectorType(type)
	{
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: this.id, propertyName: "effectorType", propertyValue: type }));
	}

	get name()
	{
		return this.getDataObject().name;
	}

	set name(name)
	{
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: this.id, propertyName: "name", propertyValue: name }));
	}

	onEffectorDataChanged()
	{
		const newDataObject = {
			...this.getDataObject(),
			effectorData: this.effector.save(),
		};
		store.dispatch(StoreActions.timeline.setElementData({ dataId: this.id, data: newDataObject }));
	}

	clone(newChannel: TimelineChannel)
	{
		const newFragment = new TimelineFragment(this.name, newChannel, this.effectorType, this.startTime, this.endTime);
		newFragment.uiInfo = Object.assign({}, this.uiInfo);
		newFragment.effector.copyValues(this.effector);
		newFragment.startTime = this.startTime;
		newFragment.endTime = this.endTime;
		return newFragment;
	}

	save()
	{
		const output: TimelineFragmentSaveData = {
			id: this.id,
			name: this.name,
			channelId: this.channel.id,
			timelineId: this.timeline.id,
			startTime: this.startTime,
			endTime: this.endTime,
			effectorType: this.effectorType,
			uiColor: this.uiInfo.color,
			effector: this.effector.save()
		};

		return output;
	}

	load(savedData : TimelineFragmentSaveData)
	{
		store.dispatch(StoreActions.timeline.reparentElementData({ dataId: this.id, newDataId: savedData.id }));
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: savedData.id, propertyName: "fragmentId", propertyValue: savedData.id }));

		this.#id = savedData.id;
		timelineElementMap.set(this.id, this);

		this.name = savedData.name;
		this.startTime = savedData.startTime;
		this.endTime = savedData.endTime;
		this.effectorType = savedData.effectorType;
		this.uiInfo.color = savedData.uiColor;
		this.effector.load(savedData.effector);
		this.onEffectorDataChanged();
	}

	export(): TimelineFragmentExportData
	{
		const output : TimelineFragmentExportData = {
			startTime: this.startTime,
			endTime: this.endTime,
			effectorType: this.effectorType,
			persistentSceneObjectIds: [] as string[]
		};

		const sceneRoot = Engine.getInstance().getSelectionRoot(this.timeline.associatedSceneId);
		assert(sceneRoot, "Scene root not found for scene id: " + this.timeline.associatedSceneId);

		output.persistentSceneObjectIds = [];
		const sceneObjects = this.channel.object.getSceneObjects();
		for (const sceneObject of sceneObjects)
		{
			if (!sceneObject.userData.removedFromScene && SceneObjectUtils.findObjectByGuid(sceneRoot, sceneObject.userData.guid))
			{
				output.persistentSceneObjectIds.push(sceneObject.userData.guid);
			}
		}

		const effectorData = this.effector.save();
		for (const key in effectorData)
		{
			output[key] = effectorData[key];
		}

		return output;
	}

	reverse(reverseTimes = false)
	{
		if (this.channel.object.hidden) return;

		if (reverseTimes)
		{
			// so that newStartTime = loopTime - oldEndTime, newEndTime = loopTime - oldStartTime
			// then reverse the effector values
			// clamp values between 0-loopTime
			const duration = this.endTime - this.startTime;
			this.startTime = MathUtils.clampScalar((this.timeline.loopTime - this.endTime), 0, this.timeline.loopTime);
			this.endTime = this.startTime + duration;
		}

		this.effector.reverse();
	}

	getName()
	{
		return this.name;
	}

	getType()
	{
		return TimelineElementTypes.Fragment;
	}

	getParent()
	{
		return this.channel;
	}

	setParent(parent : TimelineChannel)
	{
		this.#channel = parent;
	}

	getDuration()
	{
		return this.endTime - this.startTime;
	}

	createEffector(effectorType: TimelineEffectorTypes)
	{
		const ctor = EffectorMap.get(effectorType);
		if ( !ctor ) throw new Error(`Effector type ${effectorType} not found`);
		return new ctor(this);
	}

	deleteSelf()
	{
		if (this.effector)
		{
			this.effector.onDelete();
			this.effector.fragment = null;
		}

		if (this.channel)
		{
			const myIndex = this.channel.fragments.indexOf(this);
			if (myIndex > -1)
			{
				this.channel.fragments.splice(myIndex, 1);
			}
		}

		if (this.timeline)
		{
			const myIndex = this.timeline.fragments.indexOf(this);
			if (myIndex > -1)
			{
				this.timeline.fragments.splice(myIndex, 1);
			}
		}

		timelineElementMap.delete(this.id);
	}

	onDelete()
	{
		if (this.effector)	this.effector.onDelete();
		if (this.timeline)	this.timeline.removeFragment(this);
		this.previousFragmentTime = 0.0;
		store.dispatch(StoreActions.timeline.setInspectedElement(false));
	}

	onAdded()
	{
		if (this.effector) this.effector.onAdded();
		if (this.timeline) this.timeline.addFragment(this);
	}

	resetAnimations()
	{
		if (this.effector)
		{
			this.effector.resetState();
		}
	}

	update(runTime)
	{
		if (this.effector)
		{
			const fragmentDuration = this.getDuration();
			if (fragmentDuration)
			{
				this.fragmentTime = MathUtils.clamp01((runTime - this.startTime) / fragmentDuration);
				if (this.effector.isLoopableEffector() && this.fragmentTime > 0 && this.fragmentTime < 1)
				{
					const iterationDuration = fragmentDuration / this.effector.loopCount;
					const iteration = Math.floor(this.fragmentTime * fragmentDuration / iterationDuration);
					this.fragmentTime = MathUtils.clamp01((runTime - (this.startTime + iteration * iterationDuration)) / iterationDuration);
				}
				if (this.effector.isReversibleEffector() && this.effector.reversed) this.fragmentTime = 1.0 - this.fragmentTime;
				if (this.effector.easing)
				{
					const fn = EasingTypeToFunctionMap.get(this.effector.easing);
					if ( fn ) this.fragmentTime = fn(this.fragmentTime);
				}

				this.effector.update(this.fragmentTime);
				this.previousFragmentTime = this.fragmentTime;
			}
			else //hidden fragments from continue step
			{
				this.effector.update(1.0);
				this.previousFragmentTime = 1.0;
			}
		}
	}
}

export class TimelineActionTrackFragment extends TimelineFragment 
{
	constructor(name, channel, effectorType, startTime = 0, endTime = 1)
	{
	  super(name, channel, effectorType, startTime, endTime);
	}

	clone(newChannel) : TimelineFragment
	{
		const newFragment = new TimelineActionTrackFragment(this.name, newChannel, this.effectorType, this.startTime, this.endTime);
		newFragment.uiInfo = Object.assign({}, this.uiInfo);
		newFragment.effector.copyValues(this.effector);
		return newFragment as TimelineFragment;
	}

	getType()
	{
		return TimelineElementTypes.ActionTrackFragment;
	}

}
